(ns manatee.domain.task
  (:require
   #?(:clj [clj-time.core :as t]
      :cljs [cljs-time.core :as t])
   [clojure.set :as set :refer [rename-keys]]
   [medley.core :as medley]
   [utility-belt.uuid :as uuid]))

;;; Constants

(def allowed-statuses #{"done" "under-review" "rejected" "requires-approval" "rejected-requires-approval"})

(def sources [{:id :linguaskill :label "Linguaskill Speaking Submissions"}
              {:id :sandi :label "Speak and Improve Submissions"}
              {:id :sandi-v2 :label "Speak and Improve V2 Submissions"}
              {:id :data-uploaded-on-creation :label "Data Uploaded on Creation"}
              {:id :wandi :label "Write&Improve"}
              {:id :manatee-aaet :label "Manatee AAET User"}
              {:id :metrica-sys :label "Metrica Sys"}
              {:id :upskill :label "Upskill"}
              {:id :metrica-est-s :label "MLT EST-S"}])

(def part-score-types [{:id :cefr-level
                        :label "CEFR level"}
                       {:id :score
                        :label "Linguaskill score"}
                       {:id :none
                        :label "(No part scores)"}])

(def answer-score-types [{:id :cefr-level
                          :label "CEFR level"}
                         {:id :none
                          :label "(No answer scores)"}])

(def types [{:id :scoring :label "Scoring"}
            {:id :transcript-annotation :label "Transcription Annotation"}
            {:id :error-annotation :label "Error Annotation"}
            {:id :script-annotation :label "Text Annotation"}
            {:id :mwe-annotation :label "MWE Annotation"}
            {:id :segment-transcript-annotation :label "Transcription Annotation/Diarisation"}])

(def scores
  {:cefr-level {:label "CEFR level"
                :options
                [{:value "0"
                  :label "0"}
                 {:value "Pre-A1"
                  :label "Pre-A1"}
                 {:value "A1"
                  :label "A1"}
                 {:value "A1+"
                  :label "A1+"}
                 {:value "A2"
                  :label "A2"}
                 {:value "A2+"
                  :label "A2+"}
                 {:value "B1"
                  :label "B1"}
                 {:value "B1+"
                  :label "B1+"}
                 {:value "B2"
                  :label "B2"}
                 {:value "B2+"
                  :label "B2+"}
                 {:value "C1"
                  :label "C1"}
                 {:value "C1+"
                  :label "C1+"}
                 {:value "C2"
                  :label "C2"}]}

   :score {:label "Score"
           :options
           [{:value "no-attempt-made"
             :label "No Attempt Made"}
            {:value "malpractice"
             :label "Malpractice"}
            {:value "off-topic"
             :label "Off-topic"}
            {:value "0"
             :label "0.0"}
            {:value "1"
             :label "1.0"}
            {:value "1.5"
             :label "1.5"}
            {:value "2"
             :label "2.0"}
            {:value "2.5"
             :label "2.5"}
            {:value "3"
             :label "3.0"}
            {:value "3.5"
             :label "3.5"}
            {:value "4"
             :label "4.0"}
            {:value "4.5"
             :label "4.5"}
            {:value "5"
             :label "5.0"}
            {:value "5.5"
             :label "5.5"}
            {:value "6"
             :label "6.0"}]}})

(def first-stage-rejections-values
  [{:value :auto-score-too-low :label "Auto-score too low"}
   {:value :auto-part-error :label "Part error"}
   {:value :auto-answer-error :label "Answer error"}
   {:value :sandi-v2-pii :label "sandi-v2 personal data"}
   {:value :ignore-l1 :label "Ignore L1"}
   {:value :disallowed-parts :label "Allowed parts only"}
   {:value :disallowed-question-ids :label "Allowed question IDs only"}])

(def next-stage-part-rejections-values
  [{:value :part-2 :label "Part 2"}
   {:value :cefr-below-a2 :label "CEFR below A2"}
   {:value :score-below-2 :label "Score below 2"}])

(def next-stage-answer-rejections-values
  [{:value :audio-quality-below-3 :label "Audio quality below 3"}
   {:value :overall-intelligibility-below-3 :label "Overall intelligibility below 3"}
   {:value :p1-q3-or-q4 :label "Part 1, Q3 or Q4"}
   {:value :not-contains-accepted-phrases :label "Does not contain accepted phrases"}])

(def speaking-scoring-criteria ;; can't think of a better name, maybe just call it default?
  [{:value :audio-quality
    :label "Audio quality (not volume) (1 is worst, 5 is best)"}
   {:value :speaking-rate
    :label "Speaking rate (too slow - too fast) (1 is too slow, 5 is too fast, 3 is just right)"}
   {:value :register
    :label "Register (1 is very informal, 5 is very formal, 3 is neutral)"}
   {:value :pronunciation
    :label "Pronunciation (1 is worst, 5 is best)"}
   {:value :flow
    :label "Flow (stress, rhythm and intonation) (1 is worst, 5 is best)"}
   {:value :overall-intelligibility
    :label "Overall intelligibility (1 is worst, 5 is best)"}
   {:value :language-resource
    :label "Language Resource (1 is worst, 5 is best)"}
   {:value :task-achievement
    :label "Task achievement (1 is worst, 5 is best)"}])

(def ceq-scoring-criteria
  [{:value :content :label "Content"}
   {:value :content :label "Content" :score-range [0 1]}
   {:value :language :label "Language"}
   {:value :organisation :label "Organisation"}
   {:value :communicative-achievement :label "Communicative Achievement"}])

(def assessments
  ;; Assessment values (except :none) match values from external sources
  [{:value :ielts_academic
    :representation "IELTS Academic"}
   {:value :ielts_general
    :representation "IELTS General"}
   {:value :wi_prompt_relevance
    :representation "Write&Improve"}
   {:value :none
    :representation "(No assessment name)"}])

(def text-accounts #{:data-uploaded-on-creation :wandi :manatee-aaet})

(def custom-validation-rules
  [{:value :reject-only-when-content-scored-0 :label "Reject only when Content scored 0"}])

;;; Functions

(defn task-type
  "Derives a task type from task fields.
   This is a better alternative to relying on the task name for logic. Still,
   it is best to avoid it altogether and write generic code."
  ;; TODO: write generic code - this makes assumptions based on the type of the first field in the answer or part!
  [{:keys [data] :as _task-config}]
  (let [{:keys [field-type]} (or (get-in data [:answer :fields 0])
                                 (get-in data [:part :fields 0]))]
    (when field-type
      (keyword "task-type"
               (case field-type
                 :segment-transcript-annotation "transcript-annotation"
                 (:drop-down :scoring :labelled-scoring) "scoring"
                 (name field-type))))))

(defn scoring?
  [task-config]
  (-> task-config task-type (= :task-type/scoring)))

(defn error-annotation?
  [task-config]
  (-> task-config task-type (= :task-type/error-annotation)))

(defn modifiers
  [task-config]
  (get-in task-config [:data :modifiers]))

(defn modifier
  [task-config modifier-key]
  (get (modifiers task-config) modifier-key))

(defn custom-markers
  [task-config]
  (get-in task-config [:data :custom-markers]))

(defn custom-styling
  [task-config]
  (get-in task-config [:data :custom-styling]))

(defn field-by-type
  [task-config item-type field-type-pred]
  (->> task-config
       :data
       item-type
       :fields
       (medley/find-first #(field-type-pred (:field-type %)))))

(defn fields-by-item-type
  [task-config item-type]
  (get-in task-config [:data item-type :fields]))

(defn field-names-by-item-type
  [task-config item-type]
  (some->>
   (fields-by-item-type task-config item-type)
   (map :field-name)))

(defn segment-annotation-field
  [task-config]
  (field-by-type task-config :answer #{:segment-transcript-annotation}))

(defn time-markers-field
  [task-config]
  (field-by-type task-config :answer #{:time-markers}))

(defn script-annotation-field
  [task-config]
  (field-by-type task-config :answer #{:script-annotation}))

(defn non-scoring-field
  [task-config]
  (field-by-type task-config :answer (complement #{:scoring :labelled-scoring :drop-down})))

(defn form-based?
  [task-config item-type]
  (and (seq (get-in task-config [:data item-type :fields]))
       (not (segment-annotation-field task-config))))

(defn score-options
  [task-config answer-score-id]
  (when answer-score-id (let [fields (get-in task-config [:data :answer :fields])
                              options-field (filter (fn [x] (= answer-score-id (:field-name x))) fields)
                              values (:options (into {} options-field))]
                          (map (fn [x] (rename-keys x {:label :representation})) values))))

(defn answer-score-id
  "Get the answer-score id from a task-config. E.g. :cefr-level
  The id is name of the drop-down field that defines the answer scores"
  [task-config]
  (:field-name (field-by-type task-config :answer #{:drop-down})))

(defn previous-task-config-id
  [task-config task-config-id]
  (last (take-while #(not= task-config-id %)
                    (map uuid/str->uuid
                         (get-in task-config [:data :multi-stage-task-order])))))

(defn account
  "Translates task's account from string to keyword using `account-ids` from app config."
  [{{:keys [account-id]} :data :as _task-config} account-ids]
  (let [account-keys (set/map-invert account-ids)]
    (get account-keys account-id)))

(defn medium
  [{:keys [data] :as _task-config}]
  (-> data :medium keyword))

(defn scoring-field
  [task-config item-type]
  (field-by-type task-config item-type #{:scoring :labelled-scoring :drop-down}))

(defn released-submission-upload?
  [form-data]
  (and (:released-submissions-only? form-data)
       (#{:linguaskill :metrica-sys :upskill} (:source form-data))))

(defn aaet-upload?
  [form-data]
  (= :manatee-aaet (:source form-data)))

(defn merge-rejection-options
  "Merge sequences of common and task configuration specific rejection options
   into a single, deduplicated sequence."
  [common-options task-config-options]
  (let [comparable-options (fn [options]
                             ;; In order to make the options comparable for
                             ;; deduplication, ensure that each contains only
                             ;; the same keys.
                             (map #(select-keys % [:label :value])
                                  options))]
    (sort-by :label
             (distinct (concat (comparable-options common-options)
                               (comparable-options task-config-options))))))

(defn coerce-rejection-option
  [{:keys [id item-type medium value label is-archived]}]
  {:id id
   :type (keyword item-type)
   :medium (keyword medium)
   :value (keyword value)
   :label label
   :archived? is-archived})

(defn task-config->task-name
  [task-config]
  (case (task-type task-config)
    :task-type/scoring "Scoring"
    :task-type/transcript-annotation "Transcription annotation"
    :task-type/error-annotation "Error annotation"
    nil))

(defn task-config->part-description
  [task-config part-number]
  (let [{:keys [value]} (first (filter #(= part-number
                                           (:part-number %))
                                       (get-in task-config [:data :part :descriptions])))]
    (if value
      value
      (task-config->task-name task-config))))

(defn get-part-rubric-options
  "Return a mapping from part number to rubric if the task config contains part
   rubric options, `nil` otherwise."
  [{:keys [data] :as _task-config}]
  (when-let [options (get-in data [:part :rubric])]
    (reduce (fn [opts {:keys [part-number value]}]
              (assoc opts part-number value))
            {}
            options)))

(def ^:private auto-rejection-value-set
  (set (map :value (concat first-stage-rejections-values first-stage-rejections-values next-stage-answer-rejections-values))))

(defn- auto-rejection-option?
  "Return logical `true` if an option represents one of the allowed
   auto-rejection options."
  [{:keys [value] :as _option}]
  (auto-rejection-value-set value))

(defn- nil-modifiers
  "Update modifiers with nil value to false"
  [task-config path]
  (update-in task-config path
             (fn [m]
               (update-vals m (fn [v]
                                (if v v false))))))

(defn edit-task-config-data
  "Prepare the (submitted) flat task-config for update to the database task_config.data column"
  [db-data new-data]
  (let [vector-modifier (fn [modifier-name]
                          {modifier-name (mapv :value (get new-data modifier-name))})
        part-rejection-options (if (= :script-annotation (:field-type new-data))
                                 (:part-rejection-options-text new-data)
                                 (:part-rejection-options-speech new-data))
        answer-rejection-options (if (= :script-annotation (:field-type new-data))
                                   (:answer-rejection-options-text new-data)
                                   (:answer-rejection-options-speech new-data))
        modifiers (merge
                   (:modifiers db-data)
                   (select-keys
                    new-data
                    [:multi-marking?
                     :multi-reject?
                     :suppress-part-screen?
                     :avoid-rejected-items?
                     :allow-reports?
                     :allow-deferments?
                     :allow-going-back?
                     :allow-notes?
                     :reject-with-annotation?
                     :released-submissions-only?
                     :show-short-rubric?
                     :show-long-rubric?
                     :show-ai-hybrid?
                     :show-analytic-scores?
                     :hide-media?
                     :archive-task?
                     :suppress-first-iterations?
                     :show-answers-remaining?])
                   (vector-modifier :custom-validation-rules))]
    (-> db-data
        (assoc :modifiers modifiers)
        (assoc-in [:part :rejections :reasons]
                  (->> (if (:suppress-part-screen? new-data) answer-rejection-options part-rejection-options)))
        (assoc-in [:answer :rejections :reasons] (remove auto-rejection-option? answer-rejection-options))
        (assoc :first-stage-rejections (mapv :value (:first-stage-rejections new-data)))
        (assoc :next-stage-part-rejections (mapv :value (:next-stage-part-rejections new-data)))
        (assoc :next-stage-answer-rejections (mapv :value (:next-stage-answer-rejections new-data)))
        (nil-modifiers [:modifiers]))))

(defn- vec-values->keyword
  [vec]
  (mapv (fn [m] (assoc m :value (keyword (:value m)))) vec))

(defn- update-path-values->keyword
  [task-config path]
  (update-in task-config path (fn [x] (vec-values->keyword x))))

(defn- update-fields-to-keyword
  [task-config path key-name]
  (update-in task-config path
             (fn [field]
               (mapv (fn [i]
                       (if (key-name i)
                         (assoc i key-name (vec-values->keyword (key-name i)))
                         i)) field))))

(defn- update-field
  [task-config item-type field]
  (update-in task-config [:data item-type :fields]
             (fn [fields]
               (mapv (fn [f] (update f field keyword)) fields))))

(defn- update-criteria
  [task-config item-type]
  (update-in task-config [:data item-type :fields]
             (fn [fields]
               (mapv (fn [f]
                       (if (:criteria f)
                         (update f :criteria (fn [criteria]
                                               (mapv (fn [c]
                                                       (update c :value keyword)) criteria)))
                         f)) fields))))

(defn- update-vector-keyword
  [task-config modifier-name]
  (if (get-in task-config [:data modifier-name])
    (update-in task-config [:data modifier-name]
               (fn [values]
                 (mapv keyword values)))
    task-config))

(defn coerce-task-config
  [task-config]
  (some-> task-config
          (update-path-values->keyword [:data :part :rejections :reasons])
          (update-fields-to-keyword [:data :answer :fields] :criteria)
          (update-path-values->keyword [:data :answer :rejections :reasons])
          (update-field :part :field-name)
          (update-field :answer :field-name)
          (update-field :part :field-type)
          (update-field :answer :field-type)
          (update-in [:data :part :item-score :min] double)
          (update-in [:data :part :item-score :max] double)
          (update-criteria :answer)
          (update-criteria :part)
          (update-in [:data :medium] keyword)
          (update-vector-keyword :first-stage-rejections)
          (update-vector-keyword :next-stage-part-rejections)
          (update-vector-keyword :next-stage-answer-rejections)
          (update-vector-keyword :custom-validation-rules)
          (nil-modifiers [:data :modifiers])))

(defn- coerce-answer
  [answer]
  (medley/assoc-some
   {:id (uuid/generate-uuid)
    :created (t/now)}
   :question-id (:question-id answer)
   :question-number (:uploaded-answer/question-number answer)
   :text (:uploaded-answer/text answer)
   :script (:uploaded-answer/script answer)))

(defn coerce-submission
  [submission]
  (let [results (map-indexed (fn [idx part]
                               {:answers (map coerce-answer part)
                                :part (inc idx)})
                             submission)]
    {:created (t/now)
     :results results
     :answers (mapcat :answers results)
     :metadata (-> submission ffirst :uploaded-answer/metadata)}))

(defn clean-up-answers-on-submission
  [submission]
  (let [clean (partial map #(dissoc % :text :script :created))]
    (-> submission
        (update :results (partial map (fn [part]
                                        (update part :answers clean))))
        (update :answers clean))))
